/**
 * Real-User Web Vitals reporter — zero-dependency, zero-config.
 *
 * Uses the browser's PerformanceObserver for LCP + CLS + FID/INP + FCP +
 * nav timing TTFB, then batches samples and ships them with
 * `navigator.sendBeacon` on `visibilitychange` / `pagehide`. If the
 * backend doesn't ack, nothing is retried — field data is best-effort.
 *
 * Only runs in production (preview + prod). Keeps bundle size tiny.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INGEST_URL = `${API}/web-vitals`;

const samples = [];
let sent = false;

const deviceFromUa = () => {
  const ua = navigator.userAgent || "";
  if (/Mobi|iPhone|Android.+Mobile/i.test(ua)) return "mobile";
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return "tablet";
  return "desktop";
};

const push = (name, value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return;
  samples.push({ name, value });
};

const send = () => {
  if (sent || samples.length === 0) return;
  sent = true;
  const body = {
    path: window.location.pathname,
    page_url: window.location.href,
    device: deviceFromUa(),
    connection: (navigator.connection && navigator.connection.effectiveType) || null,
    user_agent: navigator.userAgent,
    session_id: sessionStorage.getItem("wt_vitals_session") || null,
    samples: samples.splice(0),
  };
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    const ok = navigator.sendBeacon && navigator.sendBeacon(INGEST_URL, blob);
    if (!ok) {
      // Fallback: non-blocking fetch with keepalive.
      fetch(INGEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* best-effort */ }
};

export function initWebVitals() {
  if (typeof window === "undefined" || !window.PerformanceObserver) return;
  if (!sessionStorage.getItem("wt_vitals_session")) {
    sessionStorage.setItem("wt_vitals_session", Math.random().toString(36).slice(2, 10));
  }

  // Nav timing TTFB.
  try {
    const [nav] = performance.getEntriesByType("navigation");
    if (nav && typeof nav.responseStart === "number") push("TTFB", nav.responseStart);
  } catch (_) {}

  // Largest Contentful Paint
  try {
    let lastLcp = 0;
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) lastLcp = e.startTime + (e.duration || 0);
    });
    po.observe({ type: "largest-contentful-paint", buffered: true });
    const flushLcp = () => { if (lastLcp > 0) push("LCP", lastLcp); };
    document.addEventListener("visibilitychange", flushLcp, { once: true });
    window.addEventListener("pagehide", flushLcp, { once: true });
  } catch (_) {}

  // First Contentful Paint
  try {
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) if (e.name === "first-contentful-paint") push("FCP", e.startTime);
    });
    po.observe({ type: "paint", buffered: true });
  } catch (_) {}

  // Cumulative Layout Shift
  try {
    let cls = 0;
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (!e.hadRecentInput) cls += e.value;
      }
    });
    po.observe({ type: "layout-shift", buffered: true });
    const flushCls = () => push("CLS", +cls.toFixed(3));
    document.addEventListener("visibilitychange", flushCls, { once: true });
    window.addEventListener("pagehide", flushCls, { once: true });
  } catch (_) {}

  // INP approximation via `event` entries (ms). Falls back to FID when unsupported.
  try {
    let worstEvent = 0;
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const d = e.duration || 0;
        if (d > worstEvent) worstEvent = d;
      }
    });
    po.observe({ type: "event", durationThreshold: 40, buffered: true });
    const flush = () => { if (worstEvent > 0) push("INP", worstEvent); };
    document.addEventListener("visibilitychange", flush, { once: true });
    window.addEventListener("pagehide", flush, { once: true });
  } catch (_) {
    try {
      const po = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) push("INP", e.processingStart - e.startTime);
      });
      po.observe({ type: "first-input", buffered: true });
    } catch (_) {}
  }

  // Beacon on tab close / background.
  const flush = () => send();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}

export default initWebVitals;
